import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Package, 
  Network,
  Cable,
  ClipboardList,
  Code2,
  FileText,
  ChevronRight,
  Menu,
  X,
  Radar,
  Eye,
  Zap,
  Plug,
  DollarSign
} from 'lucide-react';
import logoImage from '@/assets/logo.png';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const featureLinks = [
  { title: 'Asset Inventory', description: 'Complete asset lifecycle management', href: '/features/inventory', icon: Package },
  { title: 'IPAM', description: 'IP address & network management', href: '/features/ipam', icon: Network },
  { title: 'Cable Management', description: 'End-to-end cable tracking', href: '/features/cables', icon: Cable },
  { title: 'Change Management', description: 'ITIL-aligned workflows', href: '/features/changes', icon: ClipboardList },
  { title: 'Network Discovery', description: 'Automated device scanning', href: '/features/discovery', icon: Radar },
  { title: '3D Visualization', description: 'Interactive floor plans', href: '/features/visualization', icon: Eye },
  { title: 'Power Monitoring', description: 'Real-time power & PUE', href: '/features/monitoring', icon: Zap },
  { title: 'Integrations', description: 'ServiceNow, Jira & more', href: '/features/integrations', icon: Plug },
  { title: 'API & Webhooks', description: 'RESTful API integration', href: '/features/api', icon: Code2 },
  { title: 'Reports', description: 'Scheduled reports & analytics', href: '/features/reports', icon: FileText },
];

const resourceLinks = [
  { title: 'Resources', href: '/resources' },
  { title: 'Pricing', href: '/pricing', icon: DollarSign },
  { title: 'FAQ', href: '/faq' },
  { title: 'Glossary', href: '/glossary' },
];

export default function MarketingHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const isFeaturePath = location.pathname.startsWith('/features');
  const isResourcePath = ['/resources', '/faq', '/glossary'].includes(location.pathname);

  const handleMobileNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b">
      <div className="container mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2 sm:gap-3">
          <img 
            src={logoImage} 
            alt="IMRACK Logo" 
            className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-contain" 
          />
          <div>
            <span className="text-lg sm:text-xl font-bold text-foreground">IMRACK</span>
            <span className="text-xs align-top text-muted-foreground">®</span>
          </div>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
          <NavigationMenu>
            <NavigationMenuList>
              {/* Features Dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  className={`bg-transparent ${isFeaturePath ? 'text-primary' : ''}`}
                >
                  Features
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-2 p-4 md:w-[500px] md:grid-cols-2 bg-popover">
                    {featureLinks.map((feature) => (
                      <li key={feature.href}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={feature.href}
                            className={`flex items-start gap-3 w-full rounded-md p-3 text-left hover:bg-accent transition-colors ${
                              isActive(feature.href) ? 'bg-accent' : ''
                            }`}
                          >
                            <feature.icon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="text-sm font-medium text-foreground">{feature.title}</div>
                              <p className="text-xs text-muted-foreground">{feature.description}</p>
                            </div>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                    <li className="md:col-span-2 border-t pt-2 mt-2">
                      <NavigationMenuLink asChild>
                        <Link
                          to="/features"
                          className="flex items-center gap-2 w-full rounded-md p-3 text-left hover:bg-accent transition-colors text-sm font-medium text-primary"
                        >
                          View All Features
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Resources Dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  className={`bg-transparent ${isResourcePath ? 'text-primary' : ''}`}
                >
                  Resources
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[200px] gap-1 p-3 bg-popover">
                    {resourceLinks.map((link) => (
                      <li key={link.href}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={link.href}
                            className={`block rounded-md p-2 text-sm hover:bg-accent transition-colors ${
                              isActive(link.href) ? 'bg-accent font-medium' : ''
                            }`}
                          >
                            {link.title}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <Button 
            variant={isActive('/contact') ? 'secondary' : 'ghost'} 
            onClick={() => navigate('/contact')}
          >
            Contact
          </Button>
          <Button onClick={() => navigate('/auth')}>
            Sign In
          </Button>
        </nav>

        {/* Mobile Navigation */}
        <div className="flex md:hidden items-center gap-2">
          <Button size="sm" onClick={() => navigate('/auth')}>
            Sign In
          </Button>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Open menu">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px] p-0">
              <SheetHeader className="p-4 border-b">
                <SheetTitle className="flex items-center gap-2">
                  <img src={logoImage} alt="IMRACK" className="h-8 w-8 rounded-lg" />
                  <span>IMRACK®</span>
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-col py-4">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="features" className="border-b-0">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent/50">
                      Features
                    </AccordionTrigger>
                    <AccordionContent className="pb-0">
                      <div className="flex flex-col">
                        {featureLinks.map((feature) => (
                          <button
                            key={feature.href}
                            onClick={() => handleMobileNavigate(feature.href)}
                            className={`flex items-center gap-3 px-6 py-3 text-left hover:bg-accent transition-colors ${
                              isActive(feature.href) ? 'bg-accent' : ''
                            }`}
                          >
                            <feature.icon className="h-4 w-4 text-primary" />
                            <span className="text-sm">{feature.title}</span>
                          </button>
                        ))}
                        <button
                          onClick={() => handleMobileNavigate('/features')}
                          className="flex items-center gap-2 px-6 py-3 text-left hover:bg-accent text-primary text-sm font-medium"
                        >
                          View All Features
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="resources" className="border-b-0">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent/50">
                      Resources
                    </AccordionTrigger>
                    <AccordionContent className="pb-0">
                      <div className="flex flex-col">
                        {resourceLinks.map((link) => (
                          <button
                            key={link.href}
                            onClick={() => handleMobileNavigate(link.href)}
                            className={`px-6 py-3 text-left text-sm hover:bg-accent transition-colors ${
                              isActive(link.href) ? 'bg-accent font-medium' : ''
                            }`}
                          >
                            {link.title}
                          </button>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                
                <button
                  onClick={() => handleMobileNavigate('/contact')}
                  className={`px-4 py-3 text-left hover:bg-accent/50 transition-colors ${
                    isActive('/contact') ? 'bg-accent font-medium' : ''
                  }`}
                >
                  Contact
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}